import React, { useCallback, useState } from "react";
import { aiApi } from "../../api/endpoints";
import { FactChip } from "../chips/FactChip";
import { AIMandatedLabel } from "./AIMandatedLabel";

/**
 * StrategyOptionsPanel — inline panel on ProcessDetail that lets a
 * reviewer request AI-analysed recovery strategy options for the
 * current process. Deterministic dimensions above are unaffected.
 *
 * Behaviour:
 *   * On mount: no LLM call. Panel shows a description + "Analyse
 *     strategies" button.
 *   * On click: POST /processes/{pid}/strategies. Renders returned
 *     strategies as cards, each with a RECOMMENDED chip + the mandated
 *     compliance label.
 *   * On guardrail rejection (rejected=true): renders the fallback
 *     notice; deterministic surface unchanged.
 *   * Cached badge appears when payload.cached=true (same contract as
 *     ExplainFindingDialog).
 *
 * The panel is NOT a modal — it's a section on the page. Focus trap
 * a11y is not required here (nothing modal); the AIDialog primitive
 * is used only for genuinely modal surfaces.
 */
export function StrategyOptionsPanel({ workspaceId, runId, processId, processName }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const analyse = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aiApi.analyseStrategies(workspaceId, runId, processId);
      setPayload(res);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, runId, processId]);

  const rejected = payload?.rejected === true;
  const strategies = payload?.response?.strategies || [];

  return (
    <section
      data-testid="strategy-options-panel"
      aria-labelledby="strategy-options-heading"
      className="rounded-lg border border-slate-200 bg-white p-5"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2
            id="strategy-options-heading"
            className="text-sm font-semibold uppercase tracking-widest text-slate-500"
          >
            Recovery Strategy Options
          </h2>
          <p className="mt-1 text-sm text-slate-800">
            AI-analysed strategy options for <span className="font-medium">{processName}</span>.
            Deterministic dimensions above remain the source of truth.
          </p>
        </div>
        {!payload && !loading && (
          <button
            type="button"
            onClick={analyse}
            data-testid="strategy-analyse-button"
            className="inline-flex items-center gap-1 rounded border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-800 hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            <span aria-hidden="true" className="text-violet-600">✦</span>
            Analyse strategies
          </button>
        )}
        {payload && !loading && (
          <button
            type="button"
            onClick={analyse}
            data-testid="strategy-refresh-button"
            className="text-xs text-slate-500 underline hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            Re-analyse
          </button>
        )}
      </header>

      {loading && (
        <div
          data-testid="strategy-loading"
          className="mt-4 text-sm text-slate-500"
        >
          Consulting the AI reviewer…
        </div>
      )}

      {error && (
        <div
          data-testid="strategy-error"
          className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800"
        >
          {error?.body?.error?.code === "LLM_NOT_CONFIGURED" &&
            "AI is not configured on this deployment."}
          {error?.body?.error?.code === "LLM_DAILY_CAP_EXCEEDED" &&
            "The daily AI spend cap for this workspace has been reached."}
          {error?.body?.error?.code === "LLM_PROVIDER_UNAVAILABLE" &&
            "The AI provider is temporarily unavailable. Try again later."}
          {!["LLM_NOT_CONFIGURED", "LLM_DAILY_CAP_EXCEEDED", "LLM_PROVIDER_UNAVAILABLE"].includes(
            error?.body?.error?.code,
          ) && (error?.body?.error?.message || "Something went wrong.")}
        </div>
      )}

      {payload && !loading && (
        <div className="mt-5 space-y-4" data-testid="strategy-results">
          <div className="flex items-center gap-2">
            <AIMandatedLabel testId="strategy-mandated-label" />
            {payload.cached && (
              <span
                data-testid="strategy-cached-badge"
                className="text-[10px] uppercase tracking-wider text-slate-500"
              >
                cached
              </span>
            )}
          </div>

          {rejected && (
            <div
              data-testid="strategy-rejected-banner"
              className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            >
              <div className="font-medium">
                The AI-generated strategy options were rejected by the guardrail.
              </div>
              <div className="mt-1">
                Reason: <code>{payload.reason_code}</code>. The deterministic
                gap list above is unchanged.
              </div>
            </div>
          )}

          {!rejected && strategies.length === 0 && (
            <div className="text-sm text-slate-500">
              No strategies returned.
            </div>
          )}

          <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {strategies.map((s, i) => (
              <StrategyCard key={i} index={i} strategy={s} />
            ))}
          </ul>

          <footer className="mt-4 border-t border-slate-100 pt-3 text-[10px] text-slate-500">
            Model: <code>{payload.model || "—"}</code>
            {" · "}Prompt: <code>v{payload.prompt_version || "—"}</code>
            {payload.created_at && (
              <>
                {" · "}Generated <time dateTime={payload.created_at}>{payload.created_at}</time>
              </>
            )}
          </footer>
        </div>
      )}
    </section>
  );
}

const EFFORT_STYLES = {
  low: "bg-emerald-50 text-emerald-800 border-emerald-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-rose-50 text-rose-800 border-rose-200",
};

function EffortPill({ label, value }) {
  const cls = EFFORT_STYLES[value] || "bg-slate-50 text-slate-800 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`}
    >
      {label}: {value}
    </span>
  );
}

function StrategyCard({ strategy, index }) {
  const s = strategy;
  return (
    <li
      data-testid={`strategy-card-${index}`}
      className="rounded-lg border border-slate-200 bg-slate-50 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">{s.name}</h3>
        <FactChip source="RECOMMENDED" />
      </div>
      <ul className="mt-2 flex flex-wrap items-center gap-1">
        {(s.addresses || []).map((a) => (
          <li key={a}>
            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
              {a}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <EffortPill label="Effort" value={s.relative_effort} />
        <EffortPill label="Complexity" value={s.indicative_complexity} />
      </div>
      <StrategySection title="Benefits">{s.benefits}</StrategySection>
      <StrategySection title="Dependencies">{s.dependencies}</StrategySection>
      <StrategySection title="Residual issues">{s.residual_issues}</StrategySection>
      <StrategySection title="Validation required">{s.validation_required}</StrategySection>
      {s.implementation_considerations && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            Implementation considerations
          </div>
          <dl className="mt-1 space-y-1 text-xs text-slate-800">
            {["people", "process", "technology", "supplier"].map((k) =>
              s.implementation_considerations[k] ? (
                <div key={k} className="grid grid-cols-[92px_1fr] gap-2">
                  <dt className="font-medium capitalize text-slate-500">{k}</dt>
                  <dd>{s.implementation_considerations[k]}</dd>
                </div>
              ) : null,
            )}
          </dl>
        </div>
      )}
      {(s.facts_referenced || []).length > 0 && (
        <div className="mt-3 text-[10px] text-slate-500">
          References: {s.facts_referenced.map((f) => f.label).join(", ")}
        </div>
      )}
    </li>
  );
}

function StrategySection({ title, children }) {
  if (!children) return null;
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">
        {title}
      </div>
      <p className="mt-1 text-xs text-slate-800">{children}</p>
    </div>
  );
}
